import { useEffect, useState, useCallback } from 'react';
import { fetchCounts, fetchObjectTypeCount, fetchEventTypeCount, fetchTimeSpanKPI } from '../api/countsApi';

/**
 * React hook to fetch counts for OCEL dashboard tiles
 * - total events
 * - total objects
 * - object type count
 * - event type count (activities)
 *
 * Now supports batch loading from consolidated Event Log KPIs for improved performance.
 *
 * @param {string|null} activeFile - Optional file type
 * @param {object} options - Hook options
 * @param {object} options.batchData - Pre-loaded batch dataset from useEventLogKPIs
 * @returns {{data: {totalEvents:number,totalObjects:number,objectTypeCount:number,eventTypeCount:number,timeSpan:object}|null, loading: boolean, error: string|null, refresh: Function}}
 */
export const useCounts = (activeFile = null, options = {}) => {
  const { batchData } = options;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Consolidated data loading logic
  const loadData = useCallback(async (fileType = null) => {
    setLoading(true);
    setError(null);
    try {
      const effectiveFileType = fileType ?? activeFile ?? null;

      // Check if we have batch data available (preferred for performance)
      if (batchData && typeof batchData === 'object') {
        console.log('useCounts: Using batch data for counts');
        const counts = {
          totalEvents: batchData.total_events,
          totalObjects: batchData.total_objects,
        };
        const typeCount = { objectTypeCount: batchData.object_type_count };
        const evtTypeCount = { eventTypeCount: batchData.event_type_count };
        const timeSpan = batchData.time_span;

        setData({
          ...counts,
          objectTypeCount: typeCount?.objectTypeCount,
          eventTypeCount: evtTypeCount?.eventTypeCount,
          timeSpan,
        });
        return;
      }

      // Fallback to individual API calls if no batch data available
      console.log('useCounts: Falling back to individual API calls');
      const [counts, typeCount, evtTypeCount] = await Promise.all([
        fetchCounts(effectiveFileType),
        fetchObjectTypeCount(effectiveFileType),
        fetchEventTypeCount(effectiveFileType),
      ]);

      let timeSpan = null;
      try {
        timeSpan = await fetchTimeSpanKPI(effectiveFileType);
      } catch (tsErr) {
        // Silently ignore time span errors; other KPIs should still render
        console.warn('Time span KPI fetch failed (non-fatal):', tsErr?.message || tsErr);
      }

      setData({
        ...counts,
        objectTypeCount: typeCount?.objectTypeCount,
        eventTypeCount: evtTypeCount?.eventTypeCount,
        timeSpan,
      });
    } catch (e) {
      setError(e?.message || 'Failed to load counts');
    } finally {
      setLoading(false);
    }
  }, [activeFile, batchData]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // Priority: use batch data if available
      if (batchData && typeof batchData === 'object') {
        console.log('useCounts: Using batch data for counts');
        if (!cancelled) {
          const counts = {
            totalEvents: batchData.total_events,
            totalObjects: batchData.total_objects,
          };
          const typeCount = { objectTypeCount: batchData.object_type_count };
          const evtTypeCount = { eventTypeCount: batchData.event_type_count };
          const timeSpan = batchData.time_span;

          setData({
            ...counts,
            objectTypeCount: typeCount?.objectTypeCount,
            eventTypeCount: evtTypeCount?.eventTypeCount,
            timeSpan,
          });
        }
        setLoading(false);
        return;
      }

      // If batch data exists but key properties are undefined, wait for it (batch might still be loading)
      if (batchData && typeof batchData === 'object' &&
          (batchData.total_events === undefined || batchData.total_objects === undefined)) {
        console.log('useCounts: Batch data exists but not complete yet, waiting...');
        // Return without setting loading state - this will retry on next batchData change
        return;
      }

      // Fallback: individual API calls only if batch data is not available at all
      console.log('useCounts: Batch data not available, using individual API calls');
      setLoading(true);
      setError(null);
      try {
        const [counts, typeCount, evtTypeCount] = await Promise.all([
          fetchCounts(activeFile ?? null),
          fetchObjectTypeCount(activeFile ?? null),
          fetchEventTypeCount(activeFile ?? null),
        ]);

        let timeSpan = null;
        try {
          timeSpan = await fetchTimeSpanKPI(activeFile ?? null);
        } catch (tsErr) {
          console.warn('Time span KPI fetch failed (non-fatal):', tsErr?.message || tsErr);
        }

        if (!cancelled) setData({
          ...counts,
          objectTypeCount: typeCount?.objectTypeCount,
          eventTypeCount: evtTypeCount?.eventTypeCount,
          timeSpan,
        });
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load counts');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => { cancelled = true; };
  }, [activeFile, batchData]);

  return { data, loading, error, refresh: loadData };
};
